import { useControllerConnectionStatus } from '../Hooks/useControllerConnectionStatus';
import { getPhysicalIOLabel } from '../Utils/physical_io_label';
import React, { useEffect, useRef, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import Typography from '@material-ui/core/Typography';
import { useSnackbar } from 'notistack';

import { MultiIOSelect, MultiIOSelectIndex, MultiOutSelect } from '../FormWidgets/Select.js';
import getStateManager from '../StateManager.js';
import { useSnapshot, proxy } from 'valtio';

import { useTranslation } from 'react-i18next';
import { LoadSettingsDialog } from '../Utils/load_settings';
import {
  GetIndexMapping,
  multiIOHwTypeMappping,
  multiIOMapping
} from '../Constants/constants';

import MultiOutLabel, { MultiOutHeadingEditable, MultiIOLabel } from '../FormWidgets/MultiOutLabel';
import { EditableLabel } from '../FormWidgets/EditableLabel';
import { SettingGroup, ValueBox } from '../FormWidgets/SettingGroup';

import { SettingActions } from '../components/index.js';

import { makeMultiOutObject, makeSlaveInfoObject } from '../Utils/utils.js';
import {
  areValveRouteAssignmentsSupported,
  areValveRouteOverridesSupported,
  buildMultiIoSettings,
  getMultiIoIndexConfig,
  getMultiIoIndexCount,
  supportsMultiIoOverrides,
  getMultiIoEditorSession,
  recordMultiIoSubmission,
  reconcileMultiIoSubmissions,
  syncMultiIoSettings,
  retainMultiIoReadback
} from '../Utils/multi_io_settings';
import {
  buildIoOverwrites,
  getOverwriteEditorSubmissions,
  recordOverwriteSubmissions,
  reconcileOverwriteSubmissions,
  applyOverwriteSubmissions
} from '../Utils/io_overwrite_settings';
import MultiIOOverwriteControl from '../FormWidgets/MultiIOOverwriteControl';

import _ from 'lodash';
import { useParams } from 'react-router-dom';

import { settingControls, useSavedSettings, writeTempSettings, useSettingsReadback } from './settingFunctions';
import { useReceivedSettings } from '../Hooks/useTabController';

import { ConnectionStatus } from '../FormWidgets/SlaveWidgets.js';
import {
  MULTI_IO_SHORT_LABEL,
  MultiIOTable,
  MultiIOViewToggle,
  sortMultiIORows,
  useMultiIOTableSort,
  useMultiIOViewMode
} from '../components/MultiIOView.js';

const useStyles = makeStyles((theme) => ({
  box: {
    backgroundColor: theme.palette.innerContainer2.background
  }
}));

function hasMultiIOIndex(controller, port) {
  return Boolean(
    getMultiIoIndexConfig(controller)[port.func] || (controller.type === 6000 && [200, 400].includes(port.func))
  );
}

function MultiIOIndexControl({ controller, parent, port, controllerValue, onChange, disabled }) {
  const funcIndexConfig = getMultiIoIndexConfig(controller);
  if (funcIndexConfig[port.func] && !(controller.type === 6000 && [200, 400].includes(port.func))) {
    return (
      <MultiIOSelectIndex
        count={getMultiIoIndexCount(controller, parent, port.func)}
        value={port.func_index ?? ''}
        controller_value={controllerValue}
        onChange={onChange}
        disabled={disabled}
        {...(funcIndexConfig[port.func].mapping && {
          mapping: GetIndexMapping(funcIndexConfig[port.func].mapping, controller, port.zone ?? parent.zone_id)
        })}
      />
    );
  }

  if (controller.type === 6000 && [200, 400].includes(port.func)) {
    return (
      <MultiIOSelectIndex
        count={
          parent.settings.timing[port.func === 200 ? 'event_timer' : 'interval_timer']?.length ?? 0
        }
        value={port.func_index ?? ''}
        controller_value={controllerValue}
        onChange={onChange}
        disabled={disabled}
      />
    );
  }

  return null;
}

export default function MultiOutSettings({ ...other }) {
  const params = useParams();
  const { controller, parent } = getStateManager().getControllerForTab(params);
  // StateManager owns the live proxies; callers may pass display snapshots.
  return (
    <MultiOutEditor
      key={JSON.stringify([controller.id, parent.id])}
      {...other}
      controller={controller}
      parent={parent}
    />
  );
}

function MultiOutEditor({ controller, parent, ...other }) {
  const { status: connectionStatus, disabledReason: connectionDisabledReason } =
    useControllerConnectionStatus(controller);

  let { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const controllerSnapshot = useSnapshot(controller);

  let sid = controller.type === 5155 ? 'multi_io' : 'multi_out'; // tab id is "outputs"

  let slaves = useSnapshot(
    controller.zones ? controller.slaves : proxy(makeSlaveInfoObject(controller, parent))
  );
  const isMultiIOController = controller.type === 6000 || controller.type === 5155;
  // Read the snapshot on every render so Valtio keeps tracking these fields.
  const multi_outs = _.cloneDeep(makeMultiOutObject({ ...controller, slaves }, parent));

  const write_access = controllerSnapshot.access_rights.multi_io?.write === true;
  const overwriteRead =
    supportsMultiIoOverrides(controllerSnapshot) && controllerSnapshot.access_rights.multi_io?.read === true;
  const overwriteWrite =
    overwriteRead && write_access;
  const connected = controllerSnapshot.connection?.status === 'good';
  const [overwriteTarget, setOverwriteTarget] = useState(null);

  const { previousSaved, submitted } = getMultiIoEditorSession(controller, parent);
  const overwriteSubmitted = getOverwriteEditorSubmissions(controller, parent);
  if (!previousSaved.current) previousSaved.current = { parent, settings: multi_outs };
  const [values, setValues] = useState(() =>
    isMultiIOController
      ? buildMultiIoSettings(multi_outs, multi_outs, {}, submitted.current, { settings: true })
      : _.cloneDeep(parent.settings[sid])
  );
  // A stored draft has not been displayed until Restore. In particular, the
  // session baseline can predate changes received while this page was closed.
  const awaitingRestore = useRef(!_.isEmpty(parent.temp_settings[sid]));
  const [pendingClock, setPendingClock] = useState(0);
  const pendingCount = Object.keys(overwriteSubmitted.current).length;
  useEffect(() => {
    if (!pendingCount) return undefined;
    const timer = setInterval(() => setPendingClock((value) => value + 1), 1000);
    return () => clearInterval(timer);
  }, [pendingCount]);
  useEffect(() => {
    if (!isMultiIOController || controller.connection.awaiting_values) return;
    const previous = previousSaved.current;
    // A replaced/reassigned port must not inherit an old overwrite draft.
    for (const [slave, ports] of Object.entries(previous.settings)) {
      for (const [port, old] of Object.entries(ports)) {
        const live = multi_outs[slave]?.[port];
        if (
          !live ||
          ['_index', 'type_id', 'zone', 'func', 'func_index'].some(
            (field) => String(old[field]) !== String(live[field])
          )
        ) {
          const drafts = parent.temp_settings[sid] ?? {};
          const replaced = !live || old._index !== live._index;
          Object.keys(drafts)
            .filter((key) =>
              key.startsWith(`multi_out_${slave}_${port}_${replaced ? '' : 'overwr_'}`)
            )
            .forEach((key) => delete drafts[key]);
          writeTempSettings(parent);
          if (overwriteTarget?.slave === slave && overwriteTarget?.port === port)
            setOverwriteTarget(null);
        }
      }
    }
    if (_.isEmpty(parent.temp_settings[sid])) awaitingRestore.current = false;
    const pending =
      previous.parent !== parent
        ? {}
        : reconcileMultiIoSubmissions(submitted.current, multi_outs, previous.settings);
    submitted.current = pending;
    // Share pending overrides with the global editor. Reconcile its function
    // submissions against the full readback too, then display this page's ports.
    const live = {
      overwr_io: controller.overwr_io,
      multi_io: makeMultiOutObject(controller, undefined)
    };
    overwriteSubmitted.current = overwriteWrite
      ? reconcileOverwriteSubmissions(overwriteSubmitted.current, live)
      : {};
    const effective = applyOverwriteSubmissions({ multi_io: multi_outs }, overwriteSubmitted.current).multi_io;
    if (write_access && !awaitingRestore.current) {
      const retained = retainMultiIoReadback(values, multi_outs, previous.settings,
        parent.temp_settings[sid], pending);
      if (!_.isEqual(retained, parent.temp_settings[sid] ?? {})) {
        if (_.isEmpty(retained)) delete parent.temp_settings[sid];
        else parent.temp_settings[sid] = retained;
        writeTempSettings(parent);
      }
    }
    setValues((current) => {
      const synced = write_access ? syncMultiIoSettings(
        current,
        effective,
        previous.settings,
        parent.temp_settings[sid],
        pending
      ) : _.cloneDeep(effective);
      const next = buildIoOverwrites(
        synced,
        current,
        overwriteWrite ? parent.temp_settings[sid] : {},
        false
      );
      return _.isEqual(current, next) ? current : next;
    });
    previousSaved.current = { parent, settings: multi_outs };
  }, [isMultiIOController, multi_outs, parent, sid, pendingClock, overwriteWrite, overwriteTarget,
    controller, controllerSnapshot.overwr_io, controllerSnapshot.connection.awaiting_values,
    previousSaved, submitted, overwriteSubmitted]);

  console.log('multiiotab', values);
  const received = useReceivedSettings(parent, sid);
  const readback = useSettingsReadback(parent, sid, received, values, setValues, {
    enabled: !isMultiIOController, canRetain: write_access
  });
  if (!isMultiIOController) Object.keys(values ?? {}).filter((key) => /^out_\d+_(func|memo)$/.test(key))
    .forEach(readback.retainSetting);
  const controls = settingControls(controller, parent, sid, values, setValues, readback);
  const { openLoadSettingsDialog, closeLoadSettingsDialog } = useSavedSettings(
    parent,
    controls,
    sid
  );
  const { viewMode, setViewMode, selectorAvailable, showTable } = useMultiIOViewMode();
  const {
    key: tableSortKey,
    direction: tableSortDirection,
    requestSort: requestTableSort
  } = useMultiIOTableSort();

  const getSlaveInfo = (slave) => Object.values(slaves).find((item) => item.address == slave);
  const getPortState = (slave, port) =>
    getSlaveInfo(slave)?.multi_outs?.find((io) => io.id == port.id)?.state ?? -1;
  const getSavedPort = (slave, port) => multi_outs?.[slave]?.[port.id] ?? port;
  const getTableSortValue = (slave, port, key) => {
    switch (key) {
      case 'type':
        return t(
          multiIOHwTypeMappping[port.type_id ?? port.type] ?? multiIOHwTypeMappping['default']
        );
      case 'function':
        return t(multiIOMapping[port.func] ?? multiIOMapping['default']);
      case 'memo':
        return port.memo ?? '';
      case 'index':
        return port.func_index ?? -1;
      case 'state':
        return getPortState(slave, port);
      case 'io':
      default:
        return Number(port.id);
    }
  };

  const transform_sent_settings = (settings) => {
    // Legacy fixed-output controllers only serialize memo names here.
    for (let i = 1; i <= (parent.settings.multi_out_values.out_count ?? 0); i++) {
      const id = 'out_' + i + '_memo';
      if (id in settings) settings.memo_names.push({ id, name: settings[id] });
    }
    return settings;
  };

  const saveSelectIndustrial = (slave, port_id, event, value) => {
    awaitingRestore.current = false;
    let v = event.target.value;
    let vv = { ...values };
    vv[slave][port_id].func = v;
    setValues(vv);
    controls.saveTempSettings(`multi_out_${slave}_${port_id}_func`, v);
  };

  const saveSelectIndexIndustrial = (slave, port_id, event, value) => {
    awaitingRestore.current = false;
    let v = event.target.value;
    let vv = { ...values };
    vv[slave][port_id].func_index = v;
    setValues(vv);
    controls.saveTempSettings(`multi_out_${slave}_${port_id}_func_index`, v);
  };

  const saveSelectGrowbase = (index, event, value) => {
    let id = `out_${index + 1}_func`;
    let v = event.target.value;
    setValues({ ...values, [id]: v });
    controls.saveTempSettings(id, v);
  };

  const saveMemoIndustrial = (slave, port_id, event, value) => {
    awaitingRestore.current = false;
    // The dialog's legacy deletion marker is numeric; the Multi IO DTO expects
    // a string and represents deletion by an empty memo.
    value = value === -1 ? '' : value;
    let vv = { ...values };
    vv[slave][port_id].memo = value;
    setValues(vv);
    controls.saveTempSettings(`multi_out_${slave}_${port_id}_memo`, value);
  };

  const saveMemoGrowbase = (id, event, value) => {
    setValues({ ...values, [id]: value });
    controls.saveTempSettings(id, value);
  };

  if (controller.type >= 6000 || controller.type === 5155) {
    controls.restoreTempSettings = () => {
      awaitingRestore.current = false;
      let _values = { ...values };
      for (const [k, v] of Object.entries(parent.temp_settings[sid])) {
        for (let field of [
          ...(write_access ? ['func', 'func_index', 'memo'] : []),
          ...(overwriteWrite
            ? ['overwr_select', 'overwr_val_ana', 'overwr_add_sett', 'overwr_timeout']
            : [])
        ]) {
          let match = k.match(new RegExp(`^multi_out_([0-9]+)_([0-9]+)_${field}$`));
          if (match) {
            let slave = match[1];
            let port = match[2];
            if (_values[slave]?.[port]) {
              _values[slave][port][field] = field === 'memo' && v === -1 ? '' : v;
            }
          }
        }
        setValues(_values);
      }
    };

    controls.resetSettings = () => {
      awaitingRestore.current = false;
      submitted.current = {};
      overwriteSubmitted.current = Object.fromEntries(
        Object.entries(overwriteSubmitted.current).filter(([, entry]) => !entry.io)
      );
      setValues(_.cloneDeep(multi_outs));
      delete parent.temp_settings[sid];
      writeTempSettings(parent);
    };
  }

  const saveSettings = () => {
    if (controller.connection?.status !== 'good' || (!write_access && !overwriteWrite)) return false;
    if (!isMultiIOController) {
      return controls.saveSettings(transform_sent_settings, true);
    }
    const live = makeMultiOutObject(controller, controller.type === 5155 ? undefined : parent);
    overwriteSubmitted.current = reconcileOverwriteSubmissions(overwriteSubmitted.current, {
      overwr_io: controller.overwr_io,
      multi_io: makeMultiOutObject(controller, undefined)
    });
    const effective = overwriteWrite
      ? applyOverwriteSubmissions({ multi_io: live }, overwriteSubmitted.current).multi_io
      : live;
    const sent = buildMultiIoSettings(
      effective,
      values,
      parent.temp_settings[sid],
      submitted.current,
      {
        settings: write_access,
        overwrites: overwriteWrite
      },
      controller.type === 6000 ? null : getMultiIoIndexConfig(controller)
    );
    if (
      !areValveRouteAssignmentsSupported(controller, parent, sent, live) ||
      !areValveRouteOverridesSupported(controller, parent, null, null, sent, live)
    ) {
      enqueueSnackbar(t('valve_irrigation_route_unavailable'), { variant: 'error' });
      return false;
    }
    const local = Object.fromEntries(
      Object.entries(multi_outs).map(([slave, ports]) => [
        slave,
        Object.fromEntries(
          Object.keys(ports)
            .filter((port) => sent[slave]?.[port])
            .map((port) => [port, sent[slave][port]])
        )
      ])
    );
    if (!settingControls(controller, parent, sid, local, setValues).saveSettings(() => sent))
      return false;
    awaitingRestore.current = false;
    submitted.current = recordMultiIoSubmission(local, multi_outs, submitted.current);
    overwriteSubmitted.current = recordOverwriteSubmissions(
      { multi_io: sent },
      { multi_io: live },
      overwriteSubmitted.current
    );
    setValues(local);
    return true;
  };

  const overwriteControl = (slave, port) => {
    const status = (
      <MultiOutLabel
        controller={controller}
        parent={parent}
        func={getSavedPort(slave, port).func ?? ''}
        func_index={getSavedPort(slave, port).func_index ?? ''}
        value={getPortState(slave, port)}
        type={port.type_id ?? ''}
      />
    );
    if (
      !overwriteRead ||
      !['digital_out', 'analog_out'].includes(multiIOHwTypeMappping[port.type_id])
    )
      return status;
    const portId = String(port.id);
    return (
      <MultiIOOverwriteControl
        controller={controller}
        parent={parent}
        slave={slave}
        port={port}
        label={getPhysicalIOLabel({ ...controller, slaves }, slave, port.id)}
        savedPort={getSavedPort(slave, port)}
        dirty={Object.keys(parent.temp_settings[sid] ?? {}).some((key) =>
          key.startsWith(`multi_out_${slave}_${portId}_overwr_`)
        )}
        anchor={
          overwriteTarget?.slave === slave && overwriteTarget?.port === portId
            ? overwriteTarget.anchor
            : null
        }
        onOpen={(anchor) => setOverwriteTarget({ slave, port: portId, anchor })}
        onClose={() => setOverwriteTarget(null)}
        onChange={(field, value) => {
          if (!overwriteWrite) return;
          if (field === 'overwr_select' && parent.temp_settings[sid]) {
            delete parent.temp_settings[sid][`multi_out_${slave}_${portId}_overwr_deleted`];
          }
          setValues((current) => ({
            ...current,
            [slave]: {
              ...current[slave],
              [portId]: {
                ...current[slave][portId],
                [field]: value
              }
            }
          }));
          controls.saveTempSettings(`multi_out_${slave}_${portId}_${field}`, value);
        }}
        onSave={saveSettings}
        writable={overwriteWrite}
        connected={connected}
      >
        {status}
      </MultiIOOverwriteControl>
    );
  };

  const classes = useStyles();

  return (
    <React.Fragment>
      <LoadSettingsDialog
        open={openLoadSettingsDialog}
        handleClose={closeLoadSettingsDialog}
        controls={controls}
      />

      {(write_access || overwriteWrite) && (
        <SettingActions
          title={connectionDisabledReason}
          on_default={connectionStatus !== 'good'}
          onReset={controls.resetSettings}
          onSave={saveSettings}
        />
      )}

      <SettingGroup
        label={
          controller.type === 6000 || controller.type === 5155
            ? t('multifunctional_ios_heading')
            : t('multifunctional_outputs_heading')
        }
      >
        {controller.type === 6000 || controller.type === 5155 ? (
          <>
            <MultiIOViewToggle
              viewMode={viewMode}
              onChange={setViewMode}
              hidden={!selectorAvailable}
            />
            {Object.keys(values).map((slave) => (
              <div
                key={`multi-io-settings-${slave}`}
                className={classes.box}
                style={{ padding: '1em', width: '100%', marginBottom: '0.5em' }}
              >
                <span style={{ fontSize: '1.5em', display: 'flex', alignItems: 'center' }}>
                  {' '}
                  {t('slave_address_hl')}{' '}
                  <div style={{ fontFamily: 'monospace', marginLeft: '0.5em' }}>
                    {slave.toString()}
                  </div>
                  <ConnectionStatus
                    controllerStatus={controller?.connection?.status}
                    slave={getSlaveInfo(slave)}
                  />
                </span>
                {showTable ? (
                  <MultiIOTable
                    ariaLabel={t('multifunctional_ios_heading')}
                    minWidth={1050}
                    sortKey={tableSortKey}
                    sortDirection={tableSortDirection}
                    onSort={requestTableSort}
                    columns={[
                      { key: 'io', label: MULTI_IO_SHORT_LABEL, width: '21%' },
                      { key: 'memo', label: t('custom_memo_name'), width: '14%' },
                      { key: 'type', label: t('type'), width: '15%' },
                      { key: 'function', label: t('function'), width: '22%' },
                      { key: 'index', label: t('io_index'), width: '10%' },
                      { key: 'state', label: t('multi_io_state'), width: '18%' }
                    ]}
                  >
                    {sortMultiIORows(
                      Object.values(values[slave]).filter((v) =>
                        controller.zones ? v.type_id !== 0 : v.type !== 0
                      ),
                      tableSortKey,
                      tableSortDirection,
                      (port, key) => getTableSortValue(slave, port, key)
                    ).map((v) => (
                      <TableRow key={`multi-io-settings-${slave}-${v.id}`}>
                        <TableCell>
                          <Typography>
                            {MULTI_IO_SHORT_LABEL}{' '}
                            {getPhysicalIOLabel({ ...controller, slaves }, slave, v.id)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <EditableLabel
                            no_label
                            id={
                              'out_' +
                              (controller.zones ? Number(v.id) + 1 : Number(v.id)) +
                              '_memo'
                            }
                            value={v.memo ?? ''}
                            controller_value={multi_outs[slave]?.[v.id]?.memo ?? ''}
                            handleInput={(id, event, value) =>
                              saveMemoIndustrial(slave, v.id, event, value)
                            }
                            disabled={!write_access}
                          />
                        </TableCell>
                        <TableCell>
                          <MultiIOLabel
                            controller={controller}
                            parent={parent}
                            func={v.type_id ?? v.type ?? ''}
                            monitor={v.monitor}
                            info={v.info ?? ''}
                            info_type={v.info_type ?? v.type ?? ''}
                          />
                        </TableCell>
                        <TableCell>
                          <MultiIOSelect
                            parent={parent}
                            controller={controller}
                            value={v.func ?? ''}
                            controller_value={getSavedPort(slave, v).func ?? ''}
                            type={v.type_id ?? ''}
                            onChange={(event, value) =>
                              saveSelectIndustrial(slave, v.id, event, value)
                            }
                            disabled={!write_access}
                          />
                        </TableCell>
                        <TableCell>
                          {hasMultiIOIndex(controllerSnapshot, v) ? (
                            <MultiIOIndexControl
                              controller={controllerSnapshot}
                              parent={parent}
                              port={v}
                              controllerValue={getSavedPort(slave, v).func_index ?? ''}
                              onChange={(event, value) =>
                                saveSelectIndexIndustrial(slave, v.id, event, value)
                              }
                              disabled={!write_access}
                            />
                          ) : (
                            '—'
                          )}
                        </TableCell>
                        <TableCell>{overwriteControl(slave, v)}</TableCell>
                      </TableRow>
                    ))}
                  </MultiIOTable>
                ) : (
                  <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                    {Object.values(values[slave]).map(
                      (v) =>
                        (controller.zones ? v.type_id !== 0 : v.type !== 0) && (
                          <ValueBox key={`multi-io-settings-${slave}-${v.id}`}>
                            <MultiOutHeadingEditable
                              controller={controller}
                              parent={parent}
                              index={controller.zones ? Number(v.id) + 1 : Number(v.id)}
                              displayIndex={getPhysicalIOLabel(
                                { ...controller, slaves },
                                slave,
                                v.id
                              )}
                              memo={v.memo ?? ''}
                              handleInput={(id, event, value) =>
                                saveMemoIndustrial(slave, v.id, event, value)
                              }
                              disabled={!write_access}
                              multi_out={v}
                            />
                            <MultiIOSelect
                              parent={parent}
                              controller={controller}
                              value={v.func ?? ''}
                              controller_value={getSavedPort(slave, v).func ?? ''}
                              type={v.type_id ?? ''}
                              onChange={(event, value) =>
                                saveSelectIndustrial(slave, v.id, event, value)
                              }
                              disabled={!write_access}
                            />
                            {hasMultiIOIndex(controllerSnapshot, v) && (
                              <>
                                <br />
                                <MultiIOIndexControl
                                  controller={controllerSnapshot}
                                  parent={parent}
                                  port={v}
                                  controllerValue={getSavedPort(slave, v).func_index ?? ''}
                                  onChange={(event, value) =>
                                    saveSelectIndexIndustrial(slave, v.id, event, value)
                                  }
                                  disabled={!write_access}
                                />
                              </>
                            )}
                            <br />
                            <br />
                            {overwriteControl(slave, v)}
                            <br />
                            <MultiIOLabel
                              controller={controller}
                              parent={parent}
                              func={v.type_id ?? v.type ?? ''}
                              monitor={v.monitor}
                              info={v.info ?? ''}
                              info_type={v.info_type ?? v.type ?? ''}
                            />
                          </ValueBox>
                        )
                    )}
                  </div>
                )}
              </div>
            ))}
          </>
        ) : (
          <>
            {multi_outs.map((multi_out_index, i) => (
              <ValueBox key={`legacy-output-${i}`}>
                <MultiOutHeadingEditable
                  controller={controller}
                  parent={parent}
                  index={i + 1}
                  memo={values[`out_${i + 1}_memo`] ?? ''}
                  handleInput={saveMemoGrowbase}
                />
                <MultiOutSelect
                  value={values[`out_${i + 1}_func`] ?? ''}
                  controller_value={parent.settings[sid][`out_${i + 1}_func`] ?? ''}
                  type={values[`out_${i + 1}_type`] ?? ''}
                  name={multi_out_index[0]}
                  controller={controller}
                  outputIndex={i + 1}
                  onChange={(event, value) => saveSelectGrowbase(i, event, value)}
                  disabled={!write_access}
                />
              </ValueBox>
            ))}
          </>
        )}
      </SettingGroup>

      {(write_access || overwriteWrite) && (
        <SettingActions
          title={connectionDisabledReason}
          on_default={connectionStatus !== 'good'}
          onReset={controls.resetSettings}
          onSave={saveSettings}
        />
      )}
    </React.Fragment>
  );
}
